import React, { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Modal, Button } from "antd";
import WidgetProducer from "../../Shared/WidgetProducer";
import { getProductAvatar } from "../../../utils/helper";
import { isNilOrEmpty } from "@salespark/toolkit";
import CustomButton from "Components/CustomButton";

const { Title } = Typography;

const Product = ({ productData, producer, priceOption, isMobile, isWidget, customStyle }) => {
  const { t } = useTranslation();
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descTruncated, setDescTruncated] = useState(false);
  const descRef = useRef(null);
  const description = productData?.information?.description;

  // "Ver mais" only when the 2-line clamp actually truncates the text
  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el) return;
    const measure = () => setDescTruncated(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [description, isWidget]);

  const ProductNameMemo = React.memo(({ information }) => {
    return <ProductName information={information} />;
  });

  const getProductImage = (p_info) => {
    let image_url = p_info?.image;
    if (isNilOrEmpty(image_url)) {
      image_url = getProductAvatar(p_info);
    }
    return image_url;
  };

  const ProductName = ({ information }) => {
    if (!information) return <></>;

    let Image = <></>;
    const imageUrl = getProductImage(information);

    if (isNilOrEmpty(imageUrl) === false) {
      Image = <img onClick={() => setShowImageModal(true)} height={60} src={`${imageUrl}?width=60`} alt={information.name} className="product-image" />;
    }

    let offerName = "";
    const appendCheckoutOfferName = productData?.advanced?.customization?.append_plan_name === true;
    const publicName = priceOption?.public_name;
    if (appendCheckoutOfferName && isNilOrEmpty(publicName) === false) {
      offerName = ` - ${publicName}`;
    }

    return (
      <div className="product-title-container">
        {Image}
        <div style={{ display: "inline" }}>
          <Title level={3} className={`product-title ${information?.name.length > 40 ? "product-title-long" : ""}`}>
            {information?.name}
            {offerName}
          </Title>
          {isWidget && <WidgetProducer data={producer} />}
        </div>
      </div>
    );
  };

  return (
    <>
      <div>
        <ProductNameMemo information={productData?.information} />
        {isWidget === false && (
          <>
            <p ref={descRef} className="product-description" title={description}>
              {description}
            </p>
            {descTruncated && (
              <button
                type="button"
                className="product-desc-more"
                style={{ color: customStyle?.link?.linkColor }}
                onClick={() => setShowDescModal(true)}
              >
                {t("product.showMore")}
              </button>
            )}
          </>
        )}
      </div>

      <Modal
        closable={true}
        title={productData?.information?.name}
        visible={showImageModal}
        footer={
          <Button type="primary" onClick={() => setShowImageModal(false)}>
            {t("product.close")}
          </Button>
        }
        onCancel={() => setShowImageModal(false)}
        onOk={() => setShowImageModal(false)}
      >
        <div style={{ textAlign: "center" }}>
          <img style={{ maxWidth: "100%", maxHeight: "100%" }} src={getProductImage(productData?.information)} alt={productData.information.name} />
        </div>
      </Modal>

      <Modal
        wrapClassName="product-desc-modal"
        title={productData?.information?.name}
        visible={showDescModal}
        onCancel={() => setShowDescModal(false)}
        footer={
          <CustomButton type="outline" buttonStyle={customStyle} onClick={() => setShowDescModal(false)}>
            {t("product.close")}
          </CustomButton>
        }
      >
        <div className="product-desc-modal-body" style={{ whiteSpace: "pre-line" }}>
          {productData?.information?.description}
        </div>
      </Modal>
    </>
  );
};

export default Product;
